import React from 'react';
import { Save } from 'lucide-react';

export default function ContentForm({ formData, handleInputChange, handleSubmit, handleCancel, editingId }) {
  return (
    <div className="bg-[#2a2a2a] rounded-lg p-6 mb-8">
      <h2 className="text-xl font-bold text-white mb-4">
        {editingId ? 'Edit Content' : 'Add New Content'}
      </h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Title</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Teams/Match</label>
            <input
              type="text"
              name="teams"
              value={formData.teams}
              onChange={handleInputChange}
              placeholder="e.g., Nigeria vs Egypt"
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-1">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              rows={3}
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Video URL (HLS)</label>
            <input
              type="url"
              name="video_url"
              value={formData.video_url}
              onChange={handleInputChange}
              required
              placeholder="https://example.com/video.m3u8"
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Thumbnail URL</label>
            <input
              type="url"
              name="thumbnail_url"
              value={formData.thumbnail_url}
              onChange={handleInputChange}
              required
              placeholder="https://example.com/thumbnail.jpg"
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Featured Hero Image
              <span className="ml-2 text-xs text-emerald-400 font-normal">(Homepage background override)</span>
            </label>
            <input
              type="url"
              name="hero_image"
              value={formData.hero_image}
              onChange={handleInputChange}
              placeholder="https://example.com/hero-1920x1080.jpg (optional)"
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">High-res image (1920px+) used as homepage hero background when this content is featured</p>
            
            {formData.hero_image && (
              <div className="mt-3 relative rounded-lg overflow-hidden border border-gray-700">
                <div className="aspect-[21/9] relative">
                  <img 
                    src={formData.hero_image} 
                    alt="Hero preview" 
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-[#0a0a0a]/40" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded">LIVE</span>
                      <span className="text-emerald-400 text-xs font-medium">{formData.competition_name || 'Competition'}</span>
                    </div>
                    <p className="text-white font-bold text-sm truncate">{formData.teams || formData.title || 'Match Title'}</p>
                    <p className="text-gray-400 text-xs mt-0.5">Hero preview with 40-60% gradient overlay</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Content Type</label>
            <select
              name="content_type"
              value={formData.content_type}
              onChange={handleInputChange}
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="vod">VOD (Video on Demand)</option>
              <option value="live">Live Stream</option>
              <option value="highlight">Highlight</option>
              <option value="original">Original Content</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Category</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
              <option value="match">Match</option>
              <option value="cultural">Cultural</option>
              <option value="highlight">Highlight</option>
              <option value="original">Original</option>
              <option value="documentary">Documentary</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Competition</label>
            <input
              type="text"
              name="competition_name"
              value={formData.competition_name}
              onChange={handleInputChange}
              placeholder="e.g., AFCON 2025"
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Start Time (for upcoming)</label>
            <input
              type="datetime-local"
              name="start_time"
              value={formData.start_time}
              onChange={handleInputChange}
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">Venue / Stadium</label>
            <input
              type="text"
              name="venue"
              value={formData.venue || ''}
              onChange={handleInputChange}
              placeholder="e.g., Stade Alassane Ouattara, Abidjan"
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">PPV Price ($)</label>
            <input
              type="number"
              step="0.01"
              name="ppv_price"
              value={formData.ppv_price || ''}
              onChange={handleInputChange}
              placeholder="e.g., 4.99"
              className="w-full px-3 py-2 bg-[#1a1a1a] border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            />
          </div>

          <div className="md:col-span-2 flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-white cursor-pointer">
              <input
                type="checkbox"
                name="is_live"
                checked={formData.is_live}
                onChange={handleInputChange}
                className="w-4 h-4 text-emerald-600 bg-[#1a1a1a] border-gray-600 rounded focus:ring-emerald-500"
              />
              <span>Live Now</span>
            </label>

            <label className="flex items-center gap-2 text-white cursor-pointer">
              <input
                type="checkbox"
                name="is_premium"
                checked={formData.is_premium}
                onChange={handleInputChange}
                className="w-4 h-4 text-emerald-600 bg-[#1a1a1a] border-gray-600 rounded focus:ring-emerald-500"
              />
              <span>Premium Content</span>
            </label>

            <label className="flex items-center gap-2 text-white cursor-pointer">
              <input
                type="checkbox"
                name="featured"
                checked={formData.featured}
                onChange={handleInputChange}
                className="w-4 h-4 text-emerald-600 bg-[#1a1a1a] border-gray-600 rounded focus:ring-emerald-500"
              />
              <span>Featured</span>
            </label>
          </div>
        </div>

        <div className="flex gap-4 pt-4">
          <button
            type="submit"
            className="flex items-center gap-2 px-6 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
          >
            <Save className="w-5 h-5" />
            {editingId ? 'Update' : 'Create'}
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
